import { getBaseURL, getClient } from '../BLL/clients/HttpClient';
import { ApiEndPoint } from '../BLL/Enums/ApiEndPoint';
import { logger } from './logger';

export interface ReportAutomation {
  id: number;
  tenantId: number;
  name: string;
  description?: string;
  sourceTable: 'Claims' | 'ClaimsBills' | 'Steerage';
  queryConfig: QueryConfig;
  frequency: 'hourly' | 'daily' | 'weekly' | 'monthly' | 'yearly';
  scheduleHour?: number;
  scheduleDayOfMonth?: number;
  scheduleDayOfWeek?: number;
  format: 'CSV' | 'XLSX' | 'TXT';
  deliveryChannel: 'email' | 'sftp';
  emailRecipients: string[];
  sftpConfigId?: number;
  isActive: boolean;
  lastRunAt?: string;
  nextRunAt?: string;
  createdAt: string;
  updatedAt: string;
  createdBy: number;
  createdByUser?: {
    id: number;
    email: string;
    firstname: string;
    lastname: string;
  };
  sftpConfig?: {
    id: number;
    name: string;
    host: string;
    port: number;
  };
}

export interface QueryConfig {
  columns: string[];
  filters?: FilterConfig[];
  orderBy?: OrderByConfig[];
  limit?: number;
  offset?: number;
}

export interface FilterConfig {
  field: string;
  operator: 'equals' | 'contains' | 'gt' | 'lt' | 'gte' | 'lte' | 'in' | 'notIn';
  value: any;
}

export interface OrderByConfig {
  field: string;
  direction: 'asc' | 'desc';
}

export interface CreateReportAutomationDto {
  name: string;
  description?: string;
  sourceTable: 'Claims' | 'ClaimsBills' | 'Steerage';
  queryConfig: QueryConfig;
  frequency: 'hourly' | 'daily' | 'weekly' | 'monthly' | 'yearly';
  scheduleHour?: number;
  scheduleDayOfMonth?: number;
  scheduleDayOfWeek?: number;
  format: 'CSV' | 'XLSX' | 'TXT';
  deliveryChannel: 'email' | 'sftp';
  emailRecipients: string[];
  sftpConfigId?: number;
  isActive?: boolean;
}

class AutomationService {
  private getApiClient() {
    const authBaseUrl = getBaseURL(ApiEndPoint.Auth);
    return getClient({ baseURL: authBaseUrl });
  }

  async getReportAutomations(): Promise<ReportAutomation[]> {
    try {
      const client = this.getApiClient();
      const { data } = await client.get<ReportAutomation[]>('v1/automation/reports');
      return data;
    } catch (error) {
      logger.error('Error fetching report automations', { error: error instanceof Error ? error.message : String(error) });
      throw error;
    }
  }

  async getReportAutomation(id: number): Promise<ReportAutomation> {
    try {
      const client = this.getApiClient();
      const { data } = await client.get<ReportAutomation>(`v1/automation/reports/${id}`);
      return data;
    } catch (error) {
      logger.error('Error fetching report automation', { error: error instanceof Error ? error.message : String(error), id: String(id) });
      throw error;
    }
  }

  async createReportAutomation(reportData: CreateReportAutomationDto): Promise<ReportAutomation> {
    try {
      const client = this.getApiClient();
      const { data } = await client.post<ReportAutomation>('v1/automation/reports', reportData);
      return data;
    } catch (error) {
      logger.error('Error creating report automation', { error: error instanceof Error ? error.message : String(error), reportName: reportData.name });
      throw error;
    }
  }

  async updateReportAutomation(id: number, reportData: CreateReportAutomationDto): Promise<ReportAutomation> {
    try {
      const client = this.getApiClient();
      const { data } = await client.put<ReportAutomation>(`v1/automation/reports/${id}`, reportData);
      return data;
    } catch (error) {
      logger.error('Error updating report automation', { error: error instanceof Error ? error.message : String(error), id: String(id) });
      throw error;
    }
  }

  async deleteReportAutomation(id: number): Promise<void> {
    try {
      const client = this.getApiClient();
      await client.delete(`v1/automation/reports/${id}`);
    } catch (error) {
      logger.error('Error deleting report automation', { error: error instanceof Error ? error.message : String(error), id: String(id) });
      throw error;
    }
  }

  async toggleReportAutomation(id: number): Promise<ReportAutomation> {
    try {
      const client = this.getApiClient();
      const { data } = await client.patch<ReportAutomation>(`v1/automation/reports/${id}/toggle`);
      return data;
    } catch (error) {
      logger.error('Error toggling report automation', { error: error instanceof Error ? error.message : String(error), id: String(id) });
      throw error;
    }
  }

  // Helper method to get available columns for a table
  getTableColumns(tableName: 'Claims' | 'ClaimsBills' | 'Steerage'): string[] {
    const columnsMap: Record<string, string[]> = {
      Claims: [
        'id', 'claimNumber', 'patientName', 'dateOfService', 'dateOfBirth',
        'diagnosis', 'claimAmount', 'status', 'createdAt', 'updatedAt'
      ],
      ClaimsBills: [
        'id', 'claimId', 'billNumber', 'billAmount', 'status', 
        'dateOfService', 'provider', 'createdAt', 'updatedAt'
      ],
      Steerage: [
        'id', 'referenceNumber', 'patientName', 'status', 'priority',
        'assignedTo', 'createdAt', 'updatedAt'
      ]
    };

    return columnsMap[tableName] || [];
  }
}

export const automationService = new AutomationService();
